import { DateTime } from 'luxon';
import { computed, makeAutoObservable } from 'mobx';
import {
  AccountMappingsApiResponse,
  ConversionBalanceApiResponse,
  GroupsApi,
} from '../../api/GroupApi';
import { Group } from './Group';

export enum MappingType {
  INTERCO = 'interco',
  REALISED_FX = 'realised_fx',
  INTEREST_COST = 'interest_cost',
  INTEREST_REVENUE = 'interest_revenue',
}

export type ErroredMapping = {
  fromId: number;
  type: MappingType;
  toId: number;
};

export type Mapping = {
  type: MappingType;
  code: string;
  entityId?: number;
  fromEntity?: number;
  loanId: number;
};

export class AccountMappings {
  group: Group = null;
  mappings: Map<number, Mapping[]> = new Map();
  conversionBalances: Map<number, Map<string, number>> = new Map();

  constructor(group: Group) {
    this.group = group;
    makeAutoObservable(this);
  }

  static fromApi(
    group: Group,
    apiData: AccountMappingsApiResponse[]
  ): AccountMappings {
    const mappings = new AccountMappings(group);
    mappings.parseApi(apiData);
    return mappings;
  }

  parseApi = (apiData: AccountMappingsApiResponse[]) => {
    const entities = this.group.entities.map(e => e.id);
    apiData.forEach(mapping => {
      if (
        entities.includes(mapping.fromId) &&
        (!mapping.toId || entities.includes(mapping.toId))
      ) {
        this.set(
          mapping.fromId,
          mapping.type as MappingType,
          mapping.code,
          mapping.toId,
          mapping.id
        );
      }
    });
  };

  parseConversionBalances = (apiData: ConversionBalanceApiResponse[]) => {
    apiData.forEach(balance => {
      this.setConversionBalanceWithExternalId(balance.organisationId, balance.nominalCode, balance.balance);
    });
  };

  getConversionBalance = (entityId: number, code: string): number => {
    return computed(() => {
      const nominalCode = this.group.getEntity(entityId).getNominalCode(code);
      if (!nominalCode?.externalId) {
        return 0.0;
      }

      return (
        this.conversionBalances.get(entityId)?.get(nominalCode.externalId) ||
        0.0
      );
    }).get();
  };

  setConversionBalance = (entityId: number, code: string, balance: number) => {
    const nominalCode = this.group.getEntity(entityId).getNominalCode(code);
    if (nominalCode?.externalId) {
      this.setConversionBalanceWithExternalId(entityId, nominalCode.externalId, balance);
    }
  };

  setConversionBalanceWithExternalId = (entityId: number, ncExternalId: string, balance: number) => {
    if (!this.conversionBalances.has(entityId)) {
      this.conversionBalances.set(entityId, new Map());
    }
    this.conversionBalances
      .get(entityId)
      .set(ncExternalId, balance);
  };

  toApi = (): AccountMappingsApiResponse[] => {
    const data: AccountMappingsApiResponse[] = [];

    this.mappings.forEach((mappings, fromId) => {
      mappings.forEach(mapping => {
        data.push({
          fromId,
          toId: mapping.entityId,
          type: mapping.type,
          code: mapping.code,
          id: mapping.loanId,
        });
      });
    });

    return data;
  };

  toConversionBalancesApi = (
    date: DateTime
  ): ConversionBalanceApiResponse[] => {
    const data: ConversionBalanceApiResponse[] = [];

    this.conversionBalances.forEach((balanceMap, organisationId) => {
      balanceMap.forEach((balance, nominalCode) => {
        data.push({
          organisationId,
          nominalCode,
          balance,
          date: date.toISODate(),
        });
      });
    });

    return data;
  };

  save = () =>
    GroupsApi.setAccountMappings(this.group.id, this.toApi()).then(
      this.parseApi
    );

  loadConversionBalances = () =>
    GroupsApi.getConversionBalances(this.group.id).then(
      this.parseConversionBalances
    );

  saveConversionBalances = (date: DateTime): Promise<AccountMappings> => {
    return GroupsApi.setConversionBalances(
      this.group.id,
      this.toConversionBalancesApi(date)
    ).then((response: ConversionBalanceApiResponse[]) => {
      this.parseConversionBalances(response);
      return this;
    });
  };

  has = (fromId: number, type: MappingType, toId: number = null): boolean => {
    if (!this.mappings.has(fromId)) return false;

    return (
      this.mappings
        .get(fromId)
        .filter(mapping => mapping.type === type && mapping.entityId === toId)
        .length > 0
    );
  };

  get = (fromId: number, type: MappingType, toId: number = null): string => {
    if (!this.mappings.has(fromId)) return null;
    const mapping = this.mappings
      .get(fromId)
      .filter(m => m.type === type && m.entityId === toId)[0];
    return mapping ? mapping.code : null;
  };

  getFromEntity = (fromId: number): Mapping[] => {
    if (!this.mappings.has(fromId)) return [];
    return this.mappings.get(fromId);
  };

  remove = (fromId: number, type: MappingType, toId: number = null) => {
    if (!this.mappings.has(fromId)) return;
    const existing = this.mappings
      .get(fromId)
      .findIndex(m => m.type === type && m.entityId === toId);

    if (existing >= 0) {
      this.mappings.get(fromId).splice(existing, 1);
    }
  };

  set = (
    fromId: number,
    type: MappingType,
    code: string,
    toId: number = null,
    loanId: number = null
  ) => {
    if (!this.mappings.has(fromId)) {
      this.mappings.set(fromId, []);
    }

    const existing = this.mappings
      .get(fromId)
      .findIndex(m => m.type === type && m.entityId === toId);

    if (existing >= 0) {
      this.mappings.get(fromId)[existing].code = code;
    } else {
      this.mappings.get(fromId).push({
        type,
        code,
        entityId: toId,
        fromEntity: fromId,
        loanId: loanId,
      });
    }
  };

  get isEmpty(): boolean {
    return this.mappings.size === 0;
  }
}
